import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import { DEFAULT_POLL_MS, fetchPendingRedeemRequests, type RedeemApprovalRequest } from './redeemApprovalService';

function isLive(request: RedeemApprovalRequest, now: number): boolean {
  if (request.status !== 'pending') return false;
  const expires = request.expires_at ? new Date(request.expires_at).getTime() : NaN;
  return Number.isNaN(expires) || expires > now;
}

/**
 * Redeem requests waiting for this Player. Checks right away when the app opens, regains focus
 * or the Player signs in (the usual case: they are at the counter and just opened the app),
 * then every `poll_after_ms` (server-controlled, 15 s default) while the page is visible.
 */
export function usePendingRedeemRequests() {
  const [requests, setRequests] = useState<RedeemApprovalRequest[]>([]);
  const pollMsRef = useRef(DEFAULT_POLL_MS);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setRequests([]);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const out = await fetchPendingRedeemRequests();
      pollMsRef.current = out.pollAfterMs;
      const now = Date.now();
      setRequests(out.requests.filter((r) => isLive(r, now)));
    } catch {
      /* offline or signed out — keep what we have and try again on the next tick */
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        if (typeof document === 'undefined' || document.visibilityState !== 'hidden') await refresh();
        if (!cancelled) schedule();
      }, pollMsRef.current);
    };

    const refreshNow = () => {
      void refresh();
      schedule();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshNow();
    };

    refreshNow();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, refreshNow);
    window.addEventListener('focus', refreshNow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, refreshNow);
      window.removeEventListener('focus', refreshNow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  /** Drop a request locally (decided, expired) without waiting for the next poll. */
  const dismiss = useCallback((requestId: string) => {
    setRequests((prev) => prev.filter((r) => r.id !== requestId));
  }, []);

  return { requests, refresh, dismiss };
}
